import type { HitsPerPage, SortingConfiguration, SearchFacet } from 'vue-instantsearch/vue3/es';
import { MAX_POINTS_FOR_RATINGS } from './reviews';

// Cache search results from server. Defaults to 2 minutes;
export const CACHE_SEARCH_RESULTS_FOR_SECONDS: number = 2 * 60;

export const CATEGORIES_ATTRIBUTES: string[] = [
  'categories.lvl0',
  'categories.lvl1',
  'categories.lvl2',
  'categories.lvl3',
  'categories.lvl4',
];

export const TYPESENSE_ROOT_LEVEL_QUERY_PARAMETERS = [
  'hierarchicalMenu',
  'query',
  'refinementList',
  'sortBy',
  'range',
  'ratingMenu',
  'page',
  'hitsPerPage',
];

export const TYPESENSE_ATTRIBUTE = {
  TAGS: 'tags',
  AVG_RATING: 'avgRating',
  BRAND: 'brand',
  PRICE: 'price',
} as const;

export const BRAND_REFINEMENT_LIST = `refinementList[${TYPESENSE_ATTRIBUTE.BRAND}]`;

export const QUERY_KEY = {
  HITS_PER_PAGE: 'hitsPerPage',
  BRAND_FILTER: `${BRAND_REFINEMENT_LIST}[0]`,
  RATING_FILTER: `ratingMenu[${TYPESENSE_ATTRIBUTE.AVG_RATING}]`,
  PAGE: 'page',
} as const;

export const TOP_STATIC_FACETS: SearchFacet[] = [
  {
    attribute: TYPESENSE_ATTRIBUTE.TAGS,
    hideTitle: true,
  },
  {
    attribute: TYPESENSE_ATTRIBUTE.PRICE,
  },
  {
    attribute: TYPESENSE_ATTRIBUTE.AVG_RATING,
  },
  {
    attribute: TYPESENSE_ATTRIBUTE.BRAND,
  },
];

// TODO uncomment * and remove fixed list after implementing proper view for facets (color picker, price slider, etc.)
// export const AVAILABLE_FACETS = ['*'];
export const AVAILABLE_FACETS: string[] = [ // use fixed list of facets (INSP-270)
  TYPESENSE_ATTRIBUTE.PRICE,
  TYPESENSE_ATTRIBUTE.AVG_RATING,
  TYPESENSE_ATTRIBUTE.BRAND,
  TYPESENSE_ATTRIBUTE.TAGS, // aka labels
  ...CATEGORIES_ATTRIBUTES,
];

export const MAX_FACETS: number = 30;

export const MIN_CHARS_FOR_SEARCH = 3;
export const MAX_LENGTH_FOR_SEARCH_INPUT = 200;

// MAX_STARS is one above the maximum rating, because in TYPESENSE this value is exclusive,
// which means the number of stars will be the provided value, minus one.
export const MAX_STARS: number = MAX_POINTS_FOR_RATINGS + 1;

export const MAX_PAGINATION = 1000000;
export const MIN_PAGINATION = 0; // TYPESENSE internally uses 0 for some requests even though 0th page generates error

export const RATING_TO_IGNORE: string = '2';

export const EXCLUDED_FACETS: string[] = [
  ...CATEGORIES_ATTRIBUTES,
  ...TOP_STATIC_FACETS.map(facetObject => facetObject.attribute),
];

export const DEFAULT_QUERY_VALUES = {
  [QUERY_KEY.HITS_PER_PAGE]: 24,
  [QUERY_KEY.BRAND_FILTER]: '',
  [QUERY_KEY.RATING_FILTER]: undefined,
  [QUERY_KEY.PAGE]: 1,
} as const;

export const ITEM_COUNT_FOR_QUICK_SEARCH: number = 5;
export const HITS_PER_PAGE_ITEMS: HitsPerPage[] = [
  {
    value: DEFAULT_QUERY_VALUES[QUERY_KEY.HITS_PER_PAGE],
    label: DEFAULT_QUERY_VALUES[QUERY_KEY.HITS_PER_PAGE].toString(),
    default: true,
  },
  { value: 12, label: '12' },
  { value: 36, label: '36' },
  { value: 48, label: '48' },
];

// Structure: { value: 'suffix of search index replica', label: 'translation key' }
export const SORTING_METHODS: SortingConfiguration[] = [
  { value: '', label: 'sortingRelevance' }, // default one
  { value: 'nameAsc', label: 'sortingNameAsc' },
  { value: 'nameDesc', label: 'sortingNameDesc' },
  { value: 'priceAsc', label: 'sortingPriceAsc' },
  { value: 'priceDesc', label: 'sortingPriceDesc' },
  { value: 'newest', label: 'sortingNewest' },
];

export const CATEGORY_PATHS: Record<string, string> = {
  FACTORY_SECONDS: 'Factory Seconds',
};

export const INDEX_ENVIRONMENT_PREFIX = {
  PRODUCTION: 'prod',
  STAGE: 'stage',
};

export const CATEGORY_FILTER_SEPARATOR = ':=';
export const CATEGORY_FILTER_INDICATOR = ' > ';

export const TYPESENSE_PROD_INDEX_START = 'prod.';
export const TYPESENSE_STAGE_INDEX_START = 'stage.';

export const TYPESENSE_GLOBAL_STATE_NAME = {
  TYPESENSE_STATE: 'typesnseState',
  TYPESENSE_STATE_SEARCHBAR: 'typesenseStateSearchBar',
} as const;

export type TypesenseGlobalStateName = ValueOf<typeof TYPESENSE_GLOBAL_STATE_NAME>;

export const TYPESENSE_LOADING_STATE = 'loading';
// stalled: The search is in progress, but the response is taking longer than expected.
export const TYPESENSE_STALLED_STATE = 'stalled';

export const BOOLEAN_ATTRIBUTE_STATE = {
  INCLUSIVE: 'INCLUSIVE',
  EXCLUSIVE: 'EXCLUSIVE',
} as const;

export const TRANSLATION_LENGTH = {
  SHORT: 'SHORT',
  LONG: 'LONG',
} as const;

export const BOOLEAN_ATTRIBUTE_LABELS = {
  [BOOLEAN_ATTRIBUTE_STATE.INCLUSIVE]: {
    [TRANSLATION_LENGTH.SHORT]: 'Show results with this criteria',
    [TRANSLATION_LENGTH.LONG]: 'Showing results with attribute',
  },
  [BOOLEAN_ATTRIBUTE_STATE.EXCLUSIVE]: {
    [TRANSLATION_LENGTH.SHORT]: 'Show results without this criteria',
    [TRANSLATION_LENGTH.LONG]: 'Showing results without attribute',
  },
};

export const TRANSLATABLE_CURRENT_REFINEMENTS = [
  'new', 'bestseller', 'bware', 'on-sale', 'topRated',
];
